<template>
  <v-container fluid>
    <PageHeaderSlot />

    <v-card class="custom-calendar custom-table">
      <v-toolbar flat class="custom-table-toolbar" height="auto">
        <v-toolbar-items class="toolbar-left-items flex-grow-1">
          <FormDatePicker
            :dateValue.sync="filter.date"
            hideDetails
            customInputClass="mr-3 mt-2 mb-3 search-input-field"
            placeholder="table.searchDate"
            :readonly="tableLoading"
            @changed="onSearchDateChange($event)"
          />

          <FormSelect
            :fieldValue.sync="filter.tutor"
            hideDetails
            class="mr-3 mt-2 mb-3 search-select-field"
            placeholder="courseTutor"
            :options="tutorOptions"
            @changed="search()"
            :readonly="tableLoading"
            enableClear
            isMultiple
            enableTooltip
          />
        </v-toolbar-items>

        <v-spacer></v-spacer>

        <v-toolbar-items class="toolbar-right-items">
          <v-btn text small color="error" @click="clearSearch()" :class="{ 'pointer-loading': tableLoading }">
            <v-icon class="mr-1 fi fi-rr-cross-small" color="error" size="18"></v-icon>
            {{ $t('table.clearSearch') }}
          </v-btn>

          <v-btn text small color="primary" @click="refreshData()" :class="{ 'pointer-loading': tableLoading }">
            <v-icon class="mr-1" color="primary" size="18"> {{ icon.mdiRefresh }} </v-icon>
            {{ $t('table.reloadTable') }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <CustomTimetable
        ref="timetable"
        :loading="tableLoading"
        :timetableData="timetableData"
        @dates-update="getTimetable($event)"
        @type-update="filter.calendarType = $event"
        :holidayData="holidayList"
        :enableCreateButton="canEdit"
        createButtonText="addTime"
        @create-clicked="handleAddTime('new', $event)"
        @event-clicked="handleAddTime('edit', $event)"
      >
        <template v-slot:[`event`]="{ event }">
          <TutorEventCard :event="event" />
        </template>
      </CustomTimetable>
    </v-card>

    <!-- Modal: 新增時間 -->
    <FormDialog
      title="addTime"
      ref="addTimeDialog"
      formRef="addTimeForm"
      disableCloseHide
      :enableCancel="isEditTime()"
      cancelBtnText="delete"
      isCancelDelete
      :maxWidth="600"
    >
      <v-row class="ma-0">
        <v-col cols="12" sm="6">
          <FormDatePicker label="date" :dateValue.sync="addTime.date" readonly required />
        </v-col>
        <v-col cols="12" sm="6">
          <FormSelect
            label="tutor"
            :fieldValue.sync="addTime.tutor"
            placeholder="selectTutor"
            :options="tutorOptions"
            required
          />
        </v-col>
        <v-col cols="12" sm="6">
          <FormRangeTimePicker
            label="time"
            dynamic
            :startTimeValue.sync="addTime.start_time"
            :endTimeValue.sync="addTime.end_time"
            :timeInterval="1"
            required
          />
        </v-col>
      </v-row>
    </FormDialog>
  </v-container>
</template>

<script>
import { mdiRefresh } from '@mdi/js'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import CustomTimetable from '@/components/CustomTimetable.vue'
import TutorEventCard from '@/components/layout/TutorEventCard.vue'
import FormDialog from '@/components/FormDialog.vue'
import FormRangeTimePicker from '@/components/formField/FormRangeTimePicker.vue'

export default {
  name: 'TutorCalendar',
  inject: ['getConfirmDialogRef'],
  components: {
    FormDatePicker,
    FormSelect,
    CustomTimetable,
    TutorEventCard,
    FormDialog,
    FormRangeTimePicker,
  },
  computed: {
    // permission & setting
    canEdit() {
      return !this.isAppMode(['T46']) || this.userType !== 'admin'
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    filter: {
      date: '',
      tutor: [],
      dateList: [],
      calendarType: '',
    },

    tutorOptions: [],
    holidayList: [],

    tableLoading: false,
    timetableData: [],

    addTime: {
      date: '',
      tutor: null,
      start_time: '',
      end_time: '',
    },

    // - static
    icon: {
      mdiRefresh,
    },
  }),
  methods: {
    async _getPayload() {
      let payload = {
        join_tutor_data: true,
      }
      const selectedSchool = this.gfGetUserSelectedSchool()
      if (selectedSchool > -1) {
        payload['center_id'] = selectedSchool
      }

      if (this.$validate.DataValid(this.filter.dateList)) {
        if (this.filter.dateList.length > 1) {
          payload['start_date'] = this.filter.dateList[0]
          payload['end_date'] = this.filter.dateList[this.filter.dateList.length - 1]
        } else {
          payload['date'] = this.filter.dateList[0]
        }
      }

      if (this.$validate.DataValid(this.filter.tutor) && this.filter.tutor.length > 0) {
        payload['staff_id_list'] = this.filter.tutor
      }

      return payload
    },
    async getTimetable(dateList, regenUrl = true) {
      if (this.$validate.DataValid(dateList)) {
        this.filter.dateList = dateList
      }

      if (regenUrl === true) {
        this._regenerateURL()
      }

      this.tableLoading = true
      try {
        const payload = await this._getPayload()
        const data = await this.$Fetcher.GetTutorCalendars(payload)
        this.timetableData = data.map(el => {
          return {
            ...el,
          }
        })
        this.tableLoading = false
      } catch (err) {
        this.$common.error(err)
        this.timetableData = []
        this.tableLoading = false
        if (err === 'abort') {
          return false
        }
      }

      return true
    },
    onSearchDateChange(val) {
      if (this.tableLoading) return

      if (!this.$validate.DataValid(val)) {
        this.$refs.timetable.setCurrentValue()
      } else {
        this.$refs.timetable.setCurrentValue(val)
      }

      this.$refs.timetable.setCalendarType('day')
      this.filter.calendarType = 'day'
      const dateList = this.$refs.timetable.getDatesList()
      this.$refs.timetable.setDateList(dateList)
      this.getTimetable(dateList)
    },
    clearSearch() {
      if (this.tableLoading) return
      this.filter.tutor = []
      this.filter.date = ''
      this.onSearchDateChange()
    },
    refreshData() {
      if (this.tableLoading) return
      this.onSearchDateChange(this.filter.date)
    },
    search() {
      if (this.tableLoading) return
      this.getTimetable()
    },

    async getTutorOptions() {
      try {
        let payload = {}
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center'] = [selectedSchool]

        const { data } = await this.$Fetcher.GetStaffs(payload)
        this.tutorOptions = data.map(el => {
          return {
            value: el.id,
            text: el.name,
          }
        })
      } catch {}
    },
    async getHolidays() {
      try {
        const { data } = await this.$Fetcher.GetHolidays()
        this.holidayList = []
        const selectedSchool = this.gfGetUserSelectedSchool()
        for (let i = 0; i < data.length; i++) {
          let newAdd = true
          for (let j = 0; j < this.holidayList.length; j++) {
            if (data[i].date === this.holidayList[j].date) {
              if (data[i].center_id === selectedSchool) {
                this.holidayList[j].name = data[i].name
              }
              newAdd = false
              break
            }
          }

          if (newAdd) {
            this.holidayList.push({
              date: data[i].date,
              name: data[i].name || '',
            })
          }
        }
      } catch (err) {
        if (err === 'abort') {
          return false
        }
      }

      return true
    },

    _getQuery() {
      this.filter.tutor = []

      const query = this.gfGetTableQuery({
        date: 'date',
        start_date: 'date',
        type: ['week', 'month', 'day'],
        tutor: 'array_int',
      })

      if (this.$validate.DataValid(query['tutor'])) this.filter.tutor = query['tutor']

      if (this.$validate.DataValid(query['date'])) this.filter.date = query['date']

      if (this.$validate.DataValid(query['type'])) {
        this.$refs.timetable.setCalendarType(query['type'])
      } else {
        this.$refs.timetable.setCalendarType('day')
      }

      if (this.$validate.DataValid(query['start_date'])) {
        this.$refs.timetable.setCurrentValue(query['start_date'])
      } else {
        this.$refs.timetable.setCurrentValue()
      }

      this.filter.dateList = this.$refs.timetable.getDatesList()
      this.$refs.timetable.setDateList(this.filter.dateList)
    },
    _regenerateURL() {
      const todayStr = this.$refs.timetable.getTodayStr()
      const q = {
        date: this.filter.date,
        start_date: this.filter.dateList[0] && this.filter.dateList[0] !== todayStr ? this.filter.dateList[0] : '',
        type: this.filter.calendarType !== 'day' ? this.filter.calendarType : '',
        tutor: this.filter.tutor.length ? this.filter.tutor.join(',') : undefined,
      }

      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) this.$router.replace({ query: newQuery })
    },

    // ========= 新增時間 =========
    isEditTime() {
      if (this.$refs.addTimeDialog) {
        const getType = this.$refs.addTimeDialog.getInfo()
        return getType === 'edit'
      }

      return false
    },
    async handleAddTime(type, value, newOpen = true) {
      if (!this.canEdit) return
      // reset form when open
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }

        this.addTime.date = ''
        this.addTime.tutor = null
        this.addTime.start_time = ''
        this.addTime.end_time = ''
        this.$refs.addTimeDialog.setInfo(type)

        if (type === 'edit') {
          const found = this.timetableData.find(el => el.id === value)
          if (found) {
            this.addTime.date = found.date
            this.addTime.tutor = found.tutor_id
            this.addTime.start_time = found.start_time
            this.addTime.end_time = found.end_time
          } else {
            return
          }
        } else {
          this.addTime.date = value
        }
      } else {
        this.$refs.addTimeDialog.setLoading(false)
      }

      // handle form submit
      const confirm = await this.$refs.addTimeDialog.show()
      if (confirm === true) {
        // -- (確認) create / edit
        const checkSchool = this.gfCheckSchoolSelected()
        if (!checkSchool) {
          this.handleAddTime(type, value, false)
          return
        }

        try {
          const selectedSchool = this.gfGetUserSelectedSchool()
          let payload = {
            tutor_id: this.addTime.tutor,
            start_time: this.addTime.start_time,
            end_time: this.addTime.end_time,
            school_id: selectedSchool,
          }

          if (type === 'edit') {
            await this.$Fetcher.UpdateTutorCalendar(value, payload)
          } else {
            payload['date'] = this.addTime.date
            await this.$Fetcher.NewTutorCalendar(payload)
          }

          this.$store.dispatch('toggleSnack', {
            message: 'message.saveSuccess',
            type: 'success',
          })
          this.$refs.addTimeDialog.hide()
          this.tableLoading = true
          setTimeout(() => {
            this.getTimetable(this.filter.dateList, false)
          }, 1000)
        } catch (err) {
          this.showAlert({
            title: 'message.saveFail',
            message: err,
            isError: true,
          })
          this.handleAddTime(type, value, false)
        }
      } else if (confirm === false && type === 'edit') {
        // -- (刪除)
        const confirmRef = this.getConfirmDialogRef()
        const confirmDelete = await confirmRef.show({ isDelete: true })
        if (confirmDelete) {
          this.$store.dispatch('setLoading', true)
          try {
            await this.$Fetcher.DeleteTutorCalendar(value)
            this.$store.dispatch('toggleSnack', {
              message: 'message.deleteSuccess',
              type: 'success',
            })
            this.$refs.addTimeDialog.hide()
            this.getTimetable(this.filter.dateList, false)
          } catch (err) {
            this.showAlert({
              title: 'message.deleteFail',
              message: err,
              isError: true,
            })
            this.handleAddTime(type, value, false)
          } finally {
            this.$store.dispatch('setLoading', false)
          }
        } else {
          this.handleAddTime(type, value, false)
        }
      } else {
        this.$refs.addTimeDialog.hide()
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: TutorCalendar')
      this.userType = this.gfGetUserType()
      this._getQuery()
      this.$store.dispatch('toggleLoadingPage', false)

      let next = await this.getTimetable(this.filter.dateList, false)
      if (next) next = await this.getHolidays()
      if (next) await this.getTutorOptions()
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.tableLoading = true
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    this.$XHR.abort()
    next()
  },
}
</script>
